import { useState, useEffect, useMemo, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { rcLogin, rcUpdateOwnPassword, rcPwForceCheck, rcPwSet, reportLoginFail, LoginError, RC_ORIGIN, type LoginErrorCode, type RcLoginResult } from '../api/rcClient';
import { tptAlert, tptConfirm } from '../stores/modalStore';
import { reportSessionStart } from '../api/loginAudit';
import { useAuthStore } from '../stores/authStore';
import { getDiag } from '../desktop/diag';
import { copyText } from '../utils/clipboard';
import i18n, { type SupportedLang } from '../i18n';
import { LANG_LABEL } from '../api/translate';
import { isIosApp } from '../utils/platform';
import { loadPasswordPolicy, passwordRules, type PasswordPolicy } from '../api/passwordPolicy';
import PasswordRules from '../components/common/PasswordRules';
import { startLoginVersionWatch } from '../api/versionGuard';
import { Icon } from '../components/icons';
import { suggestCompanyDomain, loadCompanyMailDomains, DEFAULT_COMPANY_MAIL_DOMAINS } from '../utils/companyDomain';

// 로그인 실패 분류 → 사용자 친화 메시지 (i18n 키).
const LOGIN_ERROR_KEYS: Record<LoginErrorCode, string> = {
	invalid_credentials: 'login.errInvalidCredentials',
	blocked: 'login.errBlocked',
	rate_limited: 'login.errRateLimited',
	server: 'login.errServer',
	network: 'login.errNetwork',
	totp_required: 'login.errTotpRequired',
	totp_invalid: 'login.errTotpInvalid',
	unknown: 'login.errUnknown',
};

function toFriendlyLoginMessage(err: unknown): string {
	if (err instanceof LoginError) return i18n.t(LOGIN_ERROR_KEYS[err.code]);
	// 비-LoginError (예: 이전 throw 형태 잔존) — TypeError(Failed to fetch) 도 네트워크로 처리.
	const msg = err instanceof Error ? err.message : String(err);
	if (/failed to fetch|networkerror|load failed/i.test(msg)) return i18n.t(LOGIN_ERROR_KEYS.network);
	return i18n.t(LOGIN_ERROR_KEYS.unknown);
}

// 🔁 최근 새 비번 설정 기억 (2026-08-25) — 비번찾기 재요청 시 "새 비번으로 먼저 시도" 확인창 근거. 이 기기(브라우저) 한정.
const PW_CHANGED_KEY = 'tpt-pw-changed';
const PW_CHANGED_WARN_MS = 24 * 3600_000;
function readPwChanged(): { email: string; at: number } | null {
	try {
		const raw = localStorage.getItem(PW_CHANGED_KEY);
		if (!raw) return null;
		const j = JSON.parse(raw) as { email?: unknown; at?: unknown };
		return typeof j.email === 'string' && typeof j.at === 'number' ? { email: j.email, at: j.at } : null;
	} catch { return null; }
}

// C) '나중에 변경' 유예 (2026-08-25 밤 사용자 "다시 이렇게 발생되면 안됨") — 강제화면을 24h 미룬 기록. 재발급(issuedAt 이 at 보다 뒤)이면 무효.
const PW_DEFER_KEY = 'tpt-pw-defer';
const PW_DEFER_MS = 24 * 3600_000;
function readPwDefer(): { email: string; at: number; until: number } | null {
	try {
		const raw = localStorage.getItem(PW_DEFER_KEY);
		if (!raw) return null;
		const j = JSON.parse(raw) as { email?: unknown; at?: unknown; until?: unknown };
		return typeof j.email === 'string' && typeof j.at === 'number' && typeof j.until === 'number' ? { email: j.email, at: j.at, until: j.until } : null;
	} catch { return null; }
}

// A+C) 강제화면 재진입 차단 — 서버 판정(force:true)이 틀려도 이 기기가 아는 사실로 루프를 끊는다.
//   08-25 하루 3차례(잔재 플래그·문구 불일치 400·RC 6.5 미해제) 모두 "변경했는데 또 뜬다" 로 나타났다 — 서버 해제 경로가 무엇이든
//   클라는 마지막 방어선으로 2회차 강제를 거부한다.
//   A: 이 기기에서 발급(issuedAt) 뒤에 새 비번을 설정한 기록이 있으면 통과 — 지금 로그인 성공 자체가 그 새 비번이 유효하다는 증거
//      (임시비번은 변경 즉시 무효). issuedAt 을 모르면(사이드카 미도달·구 기록) 48h 이내 기록만 인정.
//   C: '나중에 변경' 유예 24h 이내 통과 — 단 유예 뒤 재발급이면 다시 강제.
function pwForceBypass(email: string, issuedAt: string | null): boolean {
	const issued = issuedAt ? new Date(issuedAt).getTime() : NaN;
	const afterIssue = (at: number) => (Number.isFinite(issued) ? at > issued : Date.now() - at < 48 * 3600_000);
	const changed = readPwChanged();
	if (changed && changed.email === email && afterIssue(changed.at)) return true;
	const defer = readPwDefer();
	if (defer && defer.email === email && defer.until > Date.now() && afterIssue(defer.at)) return true;
	return false;
}

// 비밀번호 찾기 응답 분류
function toFriendlyResetMessage(status: number, body: { error?: string; message?: string; devTempPassword?: string; sent?: boolean; note?: string }): { ok: boolean; msg: string } {
	if (body.devTempPassword) {
		return { ok: true, msg: i18n.t('login.resetDevTemp', { pw: body.devTempPassword }) };
	}
	// 서버가 구체적 사유를 주면 그대로 보여준다 (2026-08-14) — 종전엔 2xx 를 전부 "발송했습니다"로 표시해,
	// 메일이 실패해도(=서버는 sent:false + note 로 알려줌) 사용자는 발송된 줄 알고 오지 않는 메일을 기다렸다.
	const raw = body.error || body.message;
	if (raw) return { ok: false, msg: i18n.t('login.resetFailWith', { raw }) };
	if (status >= 200 && status < 300) {
		if (body.sent === false && body.note) return { ok: false, msg: i18n.t('login.resetFailWith', { raw: body.note }) };
		return { ok: true, msg: i18n.t('login.resetSent') };
	}
	if (status === 404) return { ok: false, msg: i18n.t('login.resetNotFound') };
	if (status === 429) return { ok: false, msg: i18n.t('login.resetRateLimited') };
	if (status >= 500) return { ok: false, msg: i18n.t('login.errServer') };
	// 여기 도달 = error/message 가 비어 있는 4xx (위에서 raw 있으면 이미 반환됨)
	return { ok: false, msg: i18n.t('login.resetFail') };
}

const REMEMBER_KEY = 'tpt-remember-user';

// 🌐 로그인 화면 언어 선택 (2026-08-12 사용자 "로그인 화면이 한국어로만 있다") — 로그인 전에도 UI 언어를
//   바꿀 수 있게 한다. 옵션 라벨은 **원어명**(LANG_LABEL: 한국어 / English / Tiếng Việt …) — 현재 UI 언어를
//   못 읽는 사용자가 자기 언어를 찾아야 하는 화면이라 SettingsModal 의 "현지어(원어)" 표기와 다르게 간다.
//   저장은 i18n 의 languageChanged 리스너가('tpt-lang'), 수신 번역 언어('tpt-tx-lang')는 건드리지 않는다
//   (미설정 사용자는 getPrefLang 이 'tpt-lang' 을 폴백하므로 자연히 따라간다).
const LOGIN_LANGS: SupportedLang[] = ['ko', 'en', 'vi', 'zh', 'es'];

export default function LoginPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const setAuth = useAuthStore((s) => s.setAuth);
	// 🔤 로그인 화면은 글자 크기 '보통' 고정 (2026-08-06 사용자 — 크게 설정 시 글자 겹침).
	//   화면 배율(data-ui-scale)·메시지 글자(data-font-size)를 잠시 기본 단계로 내리고, 벗어나면 원복.
	//   저장값(localStorage)은 건드리지 않음 — 로그인 후 화면은 사용자 설정 그대로.
	useEffect(() => {
		const root = document.documentElement;
		const prevScale = root.getAttribute('data-ui-scale');
		const prevFont = root.getAttribute('data-font-size');
		root.setAttribute('data-ui-scale', 'lg');   // 'lg' = 보통 (uiScale.ts DEFAULT)
		root.setAttribute('data-font-size', 'lg');
		return () => {
			if (prevScale) root.setAttribute('data-ui-scale', prevScale); else root.removeAttribute('data-ui-scale');
			if (prevFont) root.setAttribute('data-font-size', prevFont); else root.removeAttribute('data-font-size');
		};
	}, []);
	const [user, setUser] = useState(() => {
		try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
	});
	const [password, setPassword] = useState('');
	// 🔐 2단계 인증(TOTP) 단계 (2026-08-26 D4) — 첫 시도에서 RC 가 totp-required 를 돌려주면 코드 입력칸을 열고 같은 자격에 코드를 더해 재시도
	const [totpStep, setTotpStep] = useState(false);
	const [totpCode, setTotpCode] = useState('');
	const [remember, setRemember] = useState(() => {
		try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; }
	});
	// 🔐 자동 로그인 (2026-07-11) — 기본 ON(기존 동작 유지). OFF 면 앱/브라우저 재시작 시 재로그인 (authStore 부팅 가드).
	const [autoLogin, setAutoLogin] = useState(() => {
		try { return localStorage.getItem('tpt-autologin') !== '0'; } catch { return true; }
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// 🔄 로그인 화면에서도 새 버전을 받게 (2026-08-25 사용자 — "로그인 창에서는 업데이트가 안 된다"):
	//   마운트 즉시 + 5분 주기. 웹/APK 는 서버 웹버전이 더 새로우면 새로고침, 데스크톱은 업데이트 배너 확인 강제.
	//   로그인 전이라 잃을 세션이 없고, 비번 규칙 문구 같은 로그인 화면 수정판이 입장 못 하는 사용자에게도 닿는다.
	useEffect(() => startLoginVersionWatch(), []);

	// 🔐 임시 비밀번호 로그인 → 새 비밀번호 강제 설정 (2026-08-24 사용자)
	//   비번 찾기로 발급된 임시 비번은 requirePasswordChange 플래그가 서 있다 — 로그인 응답 me 에서 감지해
	//   채팅 진입 전에 새 비번 설정 단계를 끼운다. currentPassword 는 방금 입력한 임시 비번을 그대로 사용.
	const [pwChange, setPwChange] = useState<{ r: RcLoginResult; current: string } | null>(null);
	const [newPw, setNewPw] = useState('');
	const [newPw2, setNewPw2] = useState('');
	const [pwLoading, setPwLoading] = useState(false);
	const [pwError, setPwError] = useState<string | null>(null);
	// 🔒 실제 서버 정책을 받아 규칙 체크리스트로 보여준다 (2026-08-24) — 종전엔 "14자 이상, 대/소문자…"를
	//   화면에 하드코딩해 안내했는데 운영 정책은 8자·대문자 불필요라 어긋났고, 그 탓에 하루 비번변경
	//   요청의 24%(38/161)가 400 으로 실패했다. 실패하면 플래그가 안 풀려 다음 로그인에 또 강제화면이 뜬다.
	//   🔴 규칙 문구를 여기 다시 하드코딩하지 말 것 — passwordRules() 가 서버 응답으로 만든다.
	const [pwPolicy, setPwPolicy] = useState<PasswordPolicy | null>(null);
	useEffect(() => {
		if (!pwChange || pwPolicy) return;
		void loadPasswordPolicy(pwChange.r.authToken, pwChange.r.userId, RC_ORIGIN).then(setPwPolicy);
	}, [pwChange, pwPolicy]);
	const pwRules = useMemo(() => (pwPolicy ? passwordRules(newPw, pwPolicy) : []), [newPw, pwPolicy]);
	const pwAllOk = pwRules.every((r) => r.ok);

	// 비밀번호 찾기 — 아이디 = 이메일이므로 한 칸만 받음
	// 🔐 5회 실패 안내 (2026-09-14) — 잠금이 아니라 권유. 아이디를 바꿔 입력하면 해제해 오해를 줄인다.
	const [suggestReset, setSuggestReset] = useState(false);
	const [findOpen, setFindOpen] = useState(false);
	const [findEmail, setFindEmail] = useState('');
	const [findLoading, setFindLoading] = useState(false);
	const [findMsg, setFindMsg] = useState<string | null>(null);
	// ✉️ 사내 도메인 목록(오타 경고용) — 비번찾기 폼을 열 때 1회 조회, 실패 시 기본값
	const [mailDomains, setMailDomains] = useState<string[]>(DEFAULT_COMPANY_MAIL_DOMAINS);
	useEffect(() => { if (findOpen) void loadCompanyMailDomains().then(setMailDomains); }, [findOpen]);

	const [findOk, setFindOk] = useState(false);
	const onFind = async (e: FormEvent) => {
		e.preventDefault();
		setFindMsg(null);
		setFindOk(false);
		// ✉️ 사내 도메인 오타 경고 (2026-09-04 "powelogics.kr" 로 비번찾기 → 서버는 열거 방지로 "발송했습니다" 응답,
		//   메일은 안 오고 원인은 서버 로그에만 남음). 편집거리 1~2 의 도메인이면 고쳐서 보낼지 묻는다.
		let sendEmail = findEmail.trim();
		const typo = suggestCompanyDomain(sendEmail, mailDomains);
		if (typo) {
			const fixed = `${typo.local}@${typo.suggest}`;
			const fix = await tptConfirm(
				t('login.resetDomainTypo', { typed: typo.typed, suggest: typo.suggest, fixed }),
				{ okLabel: t('login.resetDomainFix'), cancelLabel: t('login.resetDomainKeep') },
			);
			if (fix) { sendEmail = fixed; setFindEmail(fixed); }
		}
		// 🔁 재발급 루프 차단 2/2 (2026-08-25): 최근 24h 안에 이 이메일로 새 비번을 설정했다면 재발급 전에 확인 —
		//   재발급하면 방금 만든 비번이 무효가 되고 플래그가 다시 서서 강제화면이 반복된다(정지윤 사례).
		const recent = readPwChanged();
		if (recent && recent.email === sendEmail.toLowerCase() && Date.now() - recent.at < PW_CHANGED_WARN_MS) {
			const go = await tptConfirm(
				t('login.resetAfterChangeWarn', { defaultValue: '이 이메일은 최근에 새 비밀번호를 설정했습니다.\n임시 비밀번호가 아니라, 그때 설정한 새 비밀번호로 먼저 로그인해 보세요.\n\n그래도 재발급하시겠습니까? 재발급하면 지금 비밀번호는 무효가 되고 임시 비밀번호로 다시 로그인해야 합니다.' }),
				{ okLabel: t('login.resetAnyway', { defaultValue: '재발급' }), cancelLabel: t('common.cancel', { defaultValue: '취소' }) },
			);
			if (!go) return;
		}
		setFindLoading(true);
		try {
			const r = await fetch('/api/tpt/auth/reset-password', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email: sendEmail }),
			});
			const j = await r.json().catch(() => ({} as { error?: string; message?: string; devTempPassword?: string }));
			const friendly = toFriendlyResetMessage(r.status, j);
			setFindOk(friendly.ok);
			setFindMsg(friendly.msg);
		} catch (err) {
			// fetch 자체 실패 — 네트워크.
			setFindOk(false);
			setFindMsg(/failed to fetch|networkerror|load failed/i.test((err as Error).message || '')
				? t('login.errNetwork')
				: t('login.resetError'));
		} finally {
			setFindLoading(false);
		}
	};

	const onSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const inputUser = user.trim();
			const r = await rcLogin(inputUser, password, totpStep ? totpCode : undefined);
			// 🔒 보안 — RC 가 반환한 me 가 진짜 입력한 사용자인지 검증.
			// 이메일 도메인이 일치하면 OK (예: bkkim@powerlogics.kr 입력 → me.emails 에 그 이메일 있어야 함).
			// 일치 안 하면 RC 의 사용자 매핑 오류 (이메일 충돌 등) — 경고 + 진행 차단.
			if (/@/.test(inputUser)) {
				const meEmails = (r.me.emails || []).map((e) => (e.address || '').toLowerCase());
				const inputLower = inputUser.toLowerCase();
				if (meEmails.length > 0 && !meEmails.includes(inputLower)) {
					console.error('[SECURITY] 로그인 ID 와 RC 응답 사용자 mismatch!', { inputUser, meUsername: r.me.username, meEmails });
					setError(t('login.securityMismatch', { input: inputUser, user: r.me.username, emails: meEmails.join(',') || t('login.noEmail') }));
					setLoading(false);
					return;
				}
			}
			// 진단용 콘솔 출력 — 사용자가 본인 계정 정상 확인 가능
			console.info(`[TPTalk login OK] username=${r.me.username} userId=${r.userId} roles=${(r.me.roles || []).join(',')} email=${(r.me.emails?.[0]?.address) || '-'}`);
			// 🔐 임시 비번(비번 찾기 발급) 로그인 — 채팅 진입 전에 새 비번 설정 강제
			//   플래그만 믿지 않고 사이드카 판정(발급 48h 이내만 강제, 그 외 플래그는 서버가 정리 — 2026-08-25)
			if (r.me.requirePasswordChange === true) {
				const { force, issuedAt } = await rcPwForceCheck(r.authToken, r.userId);
				// A+C 우회(pwForceBypass) — 이 기기에서 이미 변경했거나 유예 중이면 서버가 force 라 해도 재강제 안 함(루프 차단)
				if (force && !pwForceBypass(inputUser.toLowerCase(), issuedAt)) { setPwChange({ r, current: password }); return; }
			}
			finishLogin(r, inputUser);
		} catch (err) {
			// 🔐 TOTP 등록 사용자 — 코드 단계로 전환(첫 요청은 코드 없이 보내 RC 가 필요 여부를 알려 준다). 틀리면 같은 단계에서 재입력.
			if (err instanceof LoginError && err.code === 'totp_required') { setTotpStep(true); setTotpCode(''); setError(null); setLoading(false); return; }
			if (err instanceof LoginError && err.code === 'totp_invalid') { setTotpStep(true); setTotpCode(''); }
			setError(toFriendlyLoginMessage(err));
			// 🔐 실패 기록 + 5회째부터 '비밀번호 찾기' 안내 (2026-09-14 사용자 "LOGIN 실패 5회 시 비번찾기로 다시 하라고")
			//   🔴 잠그지 않는다 — 계속 시도할 수 있고 안내만 뜬다([[feedback_login_gate_never_block]] 규약).
			//   네트워크 오류(서버 불통)는 사용자 잘못이 아니라 세지 않는다. 보고 실패는 무시(로그인 화면을 막지 않는다).
			if (err instanceof LoginError && err.code !== 'network' && err.code !== 'totp_required') {
				void reportLoginFail(user.trim(), err.code).then((r) => { if (r.suggestReset) setSuggestReset(true); }).catch(() => undefined);
			}
		} finally {
			setLoading(false);
		}
	};

	// 로그인 마무리 — 아이디 기억·감사 기록·스토어 반영·이동 (일반 로그인 / 비번 변경 완료 공용)
	const finishLogin = (r: RcLoginResult, inputUser: string) => {
		// ID 기억 — 체크 시 아이디만 저장 (비밀번호는 저장 안 함)
		try {
			if (remember) localStorage.setItem(REMEMBER_KEY, inputUser);
			else localStorage.removeItem(REMEMBER_KEY);
			localStorage.setItem('tpt-autologin', autoLogin ? '1' : '0'); // 자동 로그인 설정 저장
		} catch { /* private mode */ }
		// 로그인 감사 — 플랫폼(웹/데스크톱/Android) 기록. setAuth 전에 호출해 App 의 자동로그인 보고와 중복 방지.
		reportSessionStart(r.authToken, r.userId, 'password');
		// 준회원 온보딩 게이트용 — customFields.nameConfirmed(없으면 정회원 → true). App.tsx 게이트가 미확정 준회원을 /onboarding 으로 보냄.
		setAuth(r.authToken, r.userId, r.me.username, r.me.name, r.me.roles, r.me.customFields?.nameConfirmed);
		// 🔁 RequireAuth 가 넘긴 원래 경로로 복귀 — 내부 절대경로(단일 '/')만, /login·/onboarding 은 제외(루프 방지). 그 외는 /chat.
		const from = (location.state as { from?: unknown } | null)?.from;
		const safeFrom = typeof from === 'string' && /^\/(?!\/)/.test(from) && !/^\/(login|onboarding)(\/|\?|$)/.test(from) ? from : null;
		navigate(safeFrom || '/chat', { replace: true });
	};

	// 🔐 새 비밀번호 설정 제출 — 사이드카(/auth/pw-set)가 관리자 권한으로 비번을 설정하고 플래그까지 정리한 뒤, 새 비번으로 재로그인해 입장.
	//   🔴 운영 RC 6.5 는 본인 비번 변경이 플래그를 해제하지 않는다(RC 7.10+ 만) — 2026-08-25 "변경했는데 또 뜬다" 실사고 원인.
	//   🔴 2026-08-27 재발(tudq): 본인 변경(RC updateOwnBasicInfo) 뒤 후속 pw-force 호출이 실패하면 비번만 바뀌고 플래그가 남았다.
	//      해제를 후속 호출에 의존하지 않도록 설정·해제를 서버 한 요청으로 묶었다. 구 사이드카(라우트 없음)·장애 시엔 종전 경로 폴백.
	const onPwSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!pwChange) return;
		// 서버로 보내기 전에 정책 위반을 먼저 거른다 — 400 왕복 없이 어느 규칙이 안 맞는지 바로 보여준다.
		if (pwPolicy && !pwAllOk) {
			setPwError(t('login.pwForceRuleFail', { defaultValue: '아래 조건을 모두 만족해야 합니다.' }));
			return;
		}
		// 임시 비번을 그대로 새 비번으로 넣는 경우 — 서버는 error-password-same-as-current(400) 로 거절한다.
		//   그 400 도 변경 실패 = 플래그 유지 = 다음 로그인 재발이므로 여기서 먼저 막는다.
		if (newPw === pwChange.current) { setPwError(t('login.pwForceSame', { defaultValue: '임시 비밀번호와 다른 비밀번호를 사용해 주세요.' })); return; }
		if (newPw !== newPw2) { setPwError(t('login.pwForceMismatch', { defaultValue: '새 비밀번호가 서로 다릅니다.' })); return; }
		setPwError(null);
		setPwLoading(true);
		try {
			// 1) 사이드카 pw-set — 관리자 권한 설정 + requirePasswordChange 정리를 서버 한 요청으로(정책 위반 등 4xx 는 throw → 아래 catch 가 사유 표시)
			const viaSidecar = await rcPwSet(pwChange.r.authToken, pwChange.r.userId, newPw);
			if (!viaSidecar) {
				// 2) 폴백(구 사이드카·장애·임시비번 상태 아님) — 종전 경로: RC 본인 변경 + pw-force 해시 비교 정리
				await rcUpdateOwnPassword(pwChange.r.authToken, pwChange.r.userId, pwChange.current, newPw);
				await rcPwForceCheck(pwChange.r.authToken, pwChange.r.userId).catch(() => undefined);
			}
			// 3) 새 토큰 — 비번이 바뀌면 RC 가 기존 토큰을 정리할 수 있다(관리자 설정 경로 saveUser 는 기본 logout). 새 비번으로 다시
			//    로그인해 유효한 세션으로 들어간다. 재로그인이 안 되면(TOTP 필요 등) 기존 토큰으로 진행 — 무효 토큰이면 앱이 로그인
			//    화면으로 보내고, 그때는 플래그가 이미 내려가 있어 새 비번으로 바로 들어간다(재강제 없음).
			let session: RcLoginResult = pwChange.r;
			try { session = await rcLogin(user.trim(), newPw); } catch { /* 위 주석 */ }
			// 🔁 재발급 루프 차단 1/2 (2026-08-25 정지윤 사례 — 변경 성공 뒤 임시비번으로 재로그인 실패 → 비번찾기 재요청 → 또 강제):
			//   ① 이 이메일의 변경 시각을 기억(비번찾기 재요청 시 확인창 근거) ② 임시비번이 끝났음을 명시적으로 알림
			try { localStorage.setItem(PW_CHANGED_KEY, JSON.stringify({ email: user.trim().toLowerCase(), at: Date.now() })); } catch { /* private mode */ }
			await tptAlert(t('login.pwForceDone', { defaultValue: '새 비밀번호가 설정되었습니다.\n임시 비밀번호는 더 이상 사용할 수 없습니다 — 다음 로그인부터는 지금 설정한 새 비밀번호를 사용해 주세요.' }));
			finishLogin(session, user.trim());
		} catch (err) {
			const m = (err as Error)?.message || '';
			if (/same-as-current/i.test(m)) setPwError(t('login.pwForceSame', { defaultValue: '임시 비밀번호와 다른 비밀번호를 사용해 주세요.' }));
			else if (/in-history/i.test(m)) setPwError(t('login.pwForceHistory', { defaultValue: '이전에 사용한 비밀번호는 다시 사용할 수 없습니다.' }));
			else if (/password-policy|password.?policy/i.test(m)) setPwError(t('login.pwForcePolicy', { defaultValue: '비밀번호 정책에 맞지 않습니다. 아래 조건을 확인해 주세요.' }));
			else setPwError(m || t('login.errUnknown'));
		} finally {
			setPwLoading(false);
		}
	};

	// C) 나중에 변경 — 24h 유예 기록 후 입장. 서버 플래그는 그대로(다음 로그인 때 pw-force 재판정 — 유예 중이면 pwForceBypass 가 통과시킴).
	const onPwDefer = () => {
		if (!pwChange) return;
		const now = Date.now();
		try { localStorage.setItem(PW_DEFER_KEY, JSON.stringify({ email: user.trim().toLowerCase(), at: now, until: now + PW_DEFER_MS })); } catch { /* private mode */ }
		finishLogin(pwChange.r, user.trim());
	};

	// 🎨 로그인 리뉴얼 (2026-07-11) — 테마별 분기: blue/red = 밝은 배경 + 흰 카드,
	//   navy/black = 다크 풀블리드 + 글래스 입력. 3색 시그니처 라인은 4테마 공통 고정 아이덴티티.
	// 2026-08-06 사용자 — "로그인 바탕이 파랗다, 흰색으로": navy(기본 테마)도 밝은 흰 배경 + 흰 카드로.
	//   다크 로그인은 black 테마만 유지.
	const themeId = typeof document !== 'undefined' ? (document.documentElement.getAttribute('data-theme') || 'navy') : 'navy';
	const dark = themeId === 'black';
	// 입력 — 라운드 15px + 48dp 터치 타깃. 다크는 글래스(반투명), 전역 input 색 규칙(#111827)을 !important 로 이김.
	const inputCls = `h-12 w-full rounded-[15px] border pl-11 pr-4 text-ui-lg transition focus:outline-none focus:border-[var(--tpt-color-accent)] focus:ring-2 focus:ring-[rgb(var(--tpt-accent-rgb)/0.25)] ${
		dark
			? 'border-white/[.14] bg-white/[.08] !text-white placeholder:!text-white/45'
			: 'border-line bg-surface text-fg'
	}`;
	const iconCls = `pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 ${dark ? 'text-white/50' : 'text-fg-faint'}`;
	// 로그인 버튼 — 테마 primary + 눌림 상태 primaryPressed (2026-07-12 팔레트: 4테마 모두 다크 primary + 흰 글자)
	const btnCls = 'flex h-12 w-full items-center justify-center gap-2 rounded-[15px] px-4 text-ui-lg font-bold text-white transition bg-class-blue hover:brightness-110 active:!bg-[var(--tpt-color-accent-pressed)] disabled:opacity-60';
	const [showPw, setShowPw] = useState(false);
	// 언어 선택 — i18n.language 를 단일 소스로 두고, 변경 시 changeLanguage 만 호출(저장은 i18n.ts 리스너).
	const [uiLang, setUiLang] = useState<SupportedLang>(() => {
		const cur = (i18n.language || 'ko').slice(0, 2) as SupportedLang;
		return LOGIN_LANGS.includes(cur) ? cur : 'ko';
	});

	return (
		<div
			className="relative flex min-h-[100dvh] items-center justify-center overflow-x-hidden overflow-y-auto px-[30px] py-10"
			style={{ background: dark ? 'var(--tpt-color-bg-navbar)' : '#FFFFFF' }}
		>
			{/* 상단 3색 시그니처 바 — 4테마 공통 고정 (블랙·블루·레드 브랜드 아이덴티티) */}
			<div aria-hidden className="absolute inset-x-0 top-0 flex h-1.5">
				{BRAND_TRI.map((c) => <span key={c} className="flex-1" style={{ backgroundColor: c }} />)}
			</div>

			<div className={`tpt-rise-in relative w-full max-w-sm ${dark ? '' : 'rounded-2xl border border-line bg-surface px-7 pb-8 pt-8 shadow-[0_12px_40px_-14px_rgba(30,42,68,0.25)]'}`}>
				{/* 🌐 언어 선택 — 카드 최상단 우측. absolute 대신 자체 줄로 둬서 로고·워드마크와 겹칠 여지를 없앰 */}
				<div className="mb-2 flex items-center justify-end gap-1.5">
					<GlobeIcon className={dark ? 'text-white/55' : 'text-fg-faint'} />
					<label htmlFor="login-lang" className="sr-only">{t('login.language')}</label>
					<select
						id="login-lang"
						value={uiLang}
						onChange={(e) => {
							const v = e.target.value as SupportedLang;
							setUiLang(v);
							void i18n.changeLanguage(v);
						}}
						aria-label={t('login.language')}
						data-testid="login-lang"
						className={`h-9 rounded-[10px] border px-2 text-ui-base transition focus:outline-none focus:border-[var(--tpt-color-accent)] ${
							dark
								? 'border-white/[.14] bg-white/[.08] !text-white'
								: 'border-line bg-white text-fg'
						}`}
					>
						{LOGIN_LANGS.map((code) => (
							// 다크 테마에서 option 색을 인라인 지정 — native 드롭다운 목록은 select 의 흰 글자를
							// 그대로 물려받아(Windows Chrome) 흰 배경 위 흰 글자가 되는 것을 막는다.
							<option key={code} value={code} style={dark ? { color: '#F2F6FC', backgroundColor: '#1B1E26' } : undefined}>
								{LANG_LABEL[code]}
							</option>
						))}
					</select>
				</div>
				{/* 브랜드 블록 — 로고 타일(라운드 22px, 기존 에셋) + 워드마크 + 3색 언더라인 */}
				<div className="select-none text-center">
					{/* 타일 흰 배경은 inline style — 블랙 테마의 .bg-white 다크 브릿지(!important)를 이겨 다크 배경 대비 확보 */}
				<div style={{ backgroundColor: '#FFFFFF' }}
					className={`mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-[22px] ${dark ? 'shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'border border-line-subtle shadow-sm'}`} aria-hidden>
						<img src="/brand-icon.png" alt="" width={64} height={64} className="rounded-[14px]" />
					</div>
					<h1
						className="mt-4 font-black"
						style={{ fontSize: '38px', lineHeight: 1.1, letterSpacing: '-0.01em' }}
						aria-label="TPTalk"
						data-testid="login-logo"
					>
					{/* T=탑엔지니어링 레드, P=파워로직스 블루 (2026-07-13 사용자 지정 — 회사 상징색) */}
						<span style={{ color: '#E5484D' }}>T</span>
						<span style={{ color: dark ? '#5E8BFF' : '#2F6BED' }}>P</span>
						<span style={{ color: dark ? '#F2F6FC' : 'var(--tpt-color-text-primary)' }}>Talk</span>
					</h1>
					<div aria-hidden className="mx-auto mb-7 mt-3 flex h-1 w-24 overflow-hidden rounded-full">
						{BRAND_TRI.map((c) => <span key={c} className="flex-1" style={{ backgroundColor: c }} />)}
					</div>
				</div>

				{/* 🔐 임시 비밀번호 로그인 — 새 비밀번호 설정 단계 (채팅 진입 전 강제) */}
				{pwChange && (
					<form onSubmit={onPwSubmit} className="space-y-3" data-testid="pw-force-form">
						<h2 className={`text-ui-lg font-bold ${dark ? 'text-white' : 'text-fg'}`}>{t('login.pwForceTitle', { defaultValue: '새 비밀번호 설정' })}</h2>
						<p className={`text-ui-base leading-relaxed ${dark ? 'text-white/70' : 'text-fg-muted'}`}>
							{t('login.pwForceDesc', { defaultValue: '임시 비밀번호로 로그인했습니다. 계속하려면 새 비밀번호를 설정해 주세요.' })}
						</p>
						<div className="relative">
							<label htmlFor="pw-force-new" className="sr-only">{t('login.pwForceNew', { defaultValue: '새 비밀번호' })}</label>
							<LockIcon className={iconCls} />
							<input
								id="pw-force-new"
								type="password"
								placeholder={t('login.pwForceNew', { defaultValue: '새 비밀번호' })}
								value={newPw}
								onChange={(e) => setNewPw(e.target.value)}
								required
								autoFocus
								autoComplete="new-password"
								data-testid="pw-force-new"
								className={inputCls}
							/>
						</div>
						<div className="relative">
							<label htmlFor="pw-force-new2" className="sr-only">{t('login.pwForceNew2', { defaultValue: '새 비밀번호 확인' })}</label>
							<LockIcon className={iconCls} />
							<input
								id="pw-force-new2"
								type="password"
								placeholder={t('login.pwForceNew2', { defaultValue: '새 비밀번호 확인' })}
								value={newPw2}
								onChange={(e) => setNewPw2(e.target.value)}
								required
								autoComplete="new-password"
								data-testid="pw-force-new2"
								className={inputCls}
							/>
						</div>
						{/* 🔒 관리자 메뉴의 비밀번호 정책을 그대로 노출 — 개인 설정의 비밀번호 변경과 같은 컴포넌트. */}
						<PasswordRules rules={pwRules} touched={newPw.length > 0} dark={dark} testId="pw-force-rules" />
						{pwError && (
							<div role="alert" aria-live="polite"
								className="px-1 text-ui-base leading-relaxed whitespace-pre-line" style={{ color: '#F04438' }}
								data-testid="pw-force-error">{pwError}</div>
						)}
						<button type="submit" disabled={pwLoading || (pwRules.length > 0 && !pwAllOk)} data-testid="pw-force-submit" className={btnCls}>
							{pwLoading && <SpinnerIcon />}
							{t('login.pwForceSubmit', { defaultValue: '변경하고 시작하기' })}
						</button>
						{/* C) 유예 — 정책상 강제이지만 '로그인 자체를 못 하는' 상황을 만들지 않는다(2026-08-25 밤 사용자). 24h 뒤 다시 안내 */}
						<button type="button" onClick={onPwDefer} disabled={pwLoading} data-testid="pw-force-later"
							className={`w-full py-2 text-ui-sm underline underline-offset-2 ${dark ? 'text-white/60 hover:text-white/85' : 'text-fg-muted hover:text-fg'}`}>
							{t('login.pwForceLater', { defaultValue: '나중에 변경하기 (24시간 뒤 다시 안내)' })}
						</button>
					</form>
				)}

				<form onSubmit={onSubmit} className={pwChange ? 'hidden' : 'space-y-3'} data-testid="login-form">
					<div className="relative">
						<label htmlFor="login-user" className="sr-only">{t('login.userPlaceholder')}</label>
						<UserIcon className={iconCls} />
						<input
							id="login-user"
							type="text"
							placeholder={t('login.userPlaceholder')}
							value={user}
							onChange={(e) => { setUser(e.target.value); if (suggestReset) setSuggestReset(false); }}
							required
							autoFocus
							autoComplete="username"
							data-testid="login-user"
							className={inputCls}
						/>
					</div>
					<div className="relative">
						<label htmlFor="login-password" className="sr-only">{t('login.passwordPlaceholder')}</label>
						<LockIcon className={iconCls} />
						<input
							id="login-password"
							type={showPw ? 'text' : 'password'}
							placeholder={t('login.passwordPlaceholder')}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							autoComplete="current-password"
							data-testid="login-password"
							className={`${inputCls} !pr-12`}
						/>
						{/* 표시/숨김 토글 — 48dp 터치 타깃 */}
						<button type="button" onClick={() => setShowPw((v) => !v)}
							aria-label={showPw ? t('login.hidePw', { defaultValue: '비밀번호 숨기기' }) : t('login.showPw', { defaultValue: '비밀번호 표시' })}
							data-testid="login-pw-toggle"
							className={`absolute right-0 top-0 flex h-12 w-12 items-center justify-center rounded-[15px] transition ${dark ? 'text-white/50 hover:text-white/80' : 'text-fg-faint hover:text-fg'}`}>
							<EyeIcon off={showPw} />
						</button>
					</div>
					{/* 한 줄: 아이디 기억하기 · 자동 로그인 ― 비밀번호 찾기
					    🔴 flex-wrap 필수 (2026-08-10) — 세 요소가 whitespace-nowrap 이라, 글자 배율이 큰
					    모바일 기본값(xl)에서는 한 줄에 안 들어가 '비밀번호 찾기' 가 체크박스 위로 겹쳤다.
					    wrap 을 주면 자리가 모자랄 때 아래 줄로 내려가 겹치지 않는다. */}
					<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pt-0.5">
						<div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0">
							<label className={`flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap text-ui-base select-none ${dark ? 'text-white/75' : 'text-fg-muted'}`}>
								<input
									type="checkbox"
									checked={remember}
									onChange={(e) => setRemember(e.target.checked)}
									data-testid="login-remember"
									className="h-4 w-4 rounded border-line-strong text-class-blue focus:ring-class-blue"
								/>
								{t('login.rememberId')}
							</label>
							<label className={`flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap text-ui-base select-none ${dark ? 'text-white/75' : 'text-fg-muted'}`}>
								<input
									type="checkbox"
									checked={autoLogin}
									onChange={(e) => setAutoLogin(e.target.checked)}
									data-testid="login-autologin"
									className="h-4 w-4 rounded border-line-strong text-class-blue focus:ring-class-blue"
								/>
								{t('login.autoLogin', { defaultValue: '자동 로그인' })}
							</label>
						</div>
						<button
							type="button"
							onClick={() => { setFindOpen((v) => !v); setFindMsg(null); }}
							data-testid="find-password-toggle"
							className={`min-h-[44px] shrink-0 px-1 text-ui-base font-semibold underline-offset-4 transition hover:underline ${dark ? 'text-white/85' : 'text-class-blue'}`}
						>
							{t('login.findPassword')}
						</button>
					</div>
					{/* 자동 로그인 보안 가이드 — 체크 상태에서만 노출 */}
					{autoLogin && (
						<p className={`px-1 text-ui-xs leading-relaxed ${dark ? 'text-white/45' : 'text-fg-faint'}`} data-testid="login-autologin-warn">
							{t('login.autoLoginWarn', { defaultValue: '공용 PC·공공장소에서는 자동 로그인을 사용하지 마세요. 기기를 함께 쓰는 곳이라면 체크를 해제해 주세요.' })}
						</p>
					)}
					{totpStep && (
						<div className={`space-y-1.5 rounded-[15px] border p-3 ${dark ? 'border-white/[.14] bg-white/[.06]' : 'border-line-subtle bg-surface-subtle/70'}`} data-testid="login-totp">
							<div className={`text-ui-sm font-semibold ${dark ? 'text-white/85' : 'text-fg-emphasis'}`}><Icon name="lock" size={14} className="inline-block align-[-2px]" /> {t('login.totpTitle')}</div>
							<input
								id="login-totp" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
								placeholder={t('login.totpPlaceholder')} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/s+/g, ''))}
								required data-testid="login-totp-code" className={inputCls}
							/>
							<div className={`flex items-center justify-between text-ui-xs ${dark ? 'text-white/50' : 'text-fg-faint'}`}>
								<span>{t('login.totpHint')}</span>
								<button type="button" onClick={() => { setTotpStep(false); setTotpCode(''); setError(null); }} className="underline underline-offset-2">{t('login.totpBack')}</button>
							</div>
						</div>
					)}
					{error && (
						<div role="alert" aria-live="polite"
							className="px-1 text-ui-base leading-relaxed whitespace-pre-line" style={{ color: '#F04438' }}
							data-testid="login-error">{error}</div>
					)}
					{/* 🔐 5회 연속 실패 안내 (2026-09-14 사용자 "LOGIN 실패 5회 시 비번찾기로 다시 하라고 해야 함")
					    🔴 잠금이 아니다 — 계속 시도할 수 있고, 여기서 바로 비밀번호 찾기로 갈 수 있게만 한다. */}
					{suggestReset && (
						<div role="status" aria-live="polite" data-testid="login-suggest-reset"
							className={`rounded-lg px-3 py-2 text-ui-base leading-relaxed ${dark ? 'bg-amber-400/15 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
							{t('login.suggestReset', { n: 5 })}
							<button type="button" onClick={() => { setFindOpen(true); setSuggestReset(false); }} data-testid="login-suggest-reset-go"
								className="ml-1 font-semibold underline underline-offset-2">{t('login.findPassword')}</button>
						</div>
					)}
					<button
						type="submit"
						disabled={loading || (totpStep && totpCode.trim().length < 6)}
						data-testid="login-submit"
						className={btnCls}
					>
						{loading && <SpinnerIcon />}
						{loading ? t('login.loggingIn') : t('login.loginBtn')}
					</button>
					{/* 🧭 최초 이용 안내 (2026-09-01 사용자) — GW 사용자=비번찾기(임시비번) 첫 로그인 / GW 미등록=직원 가입신청 */}
					<p className={`whitespace-pre-line px-1 pt-1 text-ui-xs leading-relaxed ${dark ? 'text-white/55' : 'text-fg-muted'}`} data-testid="login-gw-guide">
						{t('login.gwGuide', { defaultValue: '그룹웨어 사용자는 [비밀번호 찾기]를 눌러서 로그인하세요.\n그룹웨어 계정이 없는 경우, [직원가입신청]으로 로그인해주세요.' })}
					</p>
				</form>

				{findOpen && !pwChange && (
					<form onSubmit={onFind} data-testid="find-password-form"
						className={`mt-4 space-y-3 rounded-[15px] border p-4 ${dark ? 'border-white/[.14] bg-white/[.06]' : 'border-line-subtle bg-surface-subtle/70'}`}>
						<p className={`text-ui-base ${dark ? 'text-white/70' : 'text-fg-muted'}`}>{t('login.findHint')}</p>
						<input
							type="email"
							placeholder={t('login.findEmailPlaceholder')}
							value={findEmail}
							onChange={(e) => setFindEmail(e.target.value)}
							required
							autoFocus
							data-testid="find-password-email"
							className={`${inputCls} !pl-4`}
						/>
						{findMsg && (
							<div role="status" aria-live="polite"
								className="px-1 text-ui-base leading-relaxed whitespace-pre-line"
								style={{ color: findOk ? (dark ? '#7DD3A8' : '#1E9E5A') : '#F04438' }}
								data-testid="find-password-msg">{findMsg}</div>
						)}
						<button
							type="submit"
							disabled={findLoading}
							data-testid="find-password-submit"
							className={btnCls}
						>
							{findLoading && <SpinnerIcon />}
							{findLoading ? t('login.processing') : t('login.sendTempPw')}
						</button>
					</form>
				)}

				{/* 📥 앱 설치 안내 — 로그인 전에도 접근 (2026-08-04 #4, /download 는 공개 라우트) */}
				{/* 🍎 iOS 앱에선 숨김 (2026-08-19 사용자 결정) — 앱 안에서 앱 설치를 안내하는 모양이 되고,
				    /download 의 iOS 카드는 '홈 화면에 추가' 안내라 심사에서 "앱 대신 웹앱을 쓰라는 안내"로
				    읽힐 수 있다. 안드로이드 앱은 현행 유지. 387행 가입신청 버튼과 같은 취지의 숨김. */}
				{!isIosApp() && (
				<button type="button" onClick={() => navigate('/download')}
					data-testid="login-install-link"
					className={`mt-4 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[15px] border px-4 py-2 text-ui-base font-semibold transition ${dark ? 'border-white/[.14] bg-white/[.06] text-white/85 hover:bg-white/[.12]' : 'border-line bg-surface-subtle/70 text-fg hover:bg-hover'}`}>
					<Icon name="download" size={16} />
					{t('login.installLink', { defaultValue: '앱 설치 안내 (Windows · Android · iPhone)' })}
				</button>
				)}

				{/* 👥 직원 가입신청 — 웹 전용 (2026-08-12). 🔴 앱(APK)에선 숨김 — 스토어 심사에 '회원가입 없음' 신고됨 */}
				{typeof window !== 'undefined' && !window.TPTAndroid && (
					<>
						{/* .vn 안내 (2026-08-24 사용자 #5 → 08-25 버튼 안 2번째 줄 라벨로) — 셀프 가입 대상(.vn 이메일)을 버튼 라벨에 명시 */}
						<button type="button" onClick={() => navigate('/signup')}
							data-testid="login-signup-link"
							className={`mt-2 flex min-h-[44px] w-full flex-col items-center justify-center rounded-[15px] border px-4 py-2 text-ui-base font-semibold leading-tight transition ${dark ? 'border-white/[.14] bg-white/[.06] text-white/85 hover:bg-white/[.12]' : 'border-line bg-surface-subtle/70 text-fg hover:bg-hover'}`}>
							<span className="flex items-center gap-2"><Icon name="users" size={16} />{t('login.signupLink', { defaultValue: '직원 가입신청' })}</span>
							<span className={`text-ui-xs font-normal ${dark ? 'text-white/50' : 'text-fg-faint'}`} data-testid="login-signup-vn-hint">
								({t('login.signupVnHint', { defaultValue: '.vn 이메일 사용자' })})
							</span>
						</button>
					</>
				)}

				{/* 하단: 3색 점 인디케이터 + 버전·서버 + 진단 로그 */}
				<div aria-hidden className="mt-7 flex items-center justify-center gap-1.5">
					{BRAND_TRI.map((c) => <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />)}
				</div>
				{/* 서버 주소 문구 제거 (2026-08-06 사용자) — 진단 로그에는 유지되어 문제 추적 가능 */}
			<p className={`mt-2 text-center text-ui-xs ${dark ? 'text-white/40' : 'text-fg-faint'}`}>TPTalk v{__APP_VERSION__}</p>
				{/* 🔧 진단 로그 — 운영에선 숨김 (2026-08-13 사용자 "이제 없어도 될 듯"). 로그 수집은 계속되며 dev 빌드에서만 노출. */}
				{import.meta.env.DEV && (
				<details className="mt-2 text-ui-xs">
					<summary className={`min-h-[44px] cursor-pointer list-none py-2 text-center ${dark ? 'text-white/40 hover:text-white/70' : 'text-fg-faint hover:text-fg'}`} role="button">
						<Icon name="chevron-right" size={12} className="inline-block align-[-2px]" /> {t('login.diagLog')}
					</summary>
					<textarea readOnly value={getDiag()} onClick={(e) => e.currentTarget.select()}
						aria-label={t('login.diagLog')}
						className={`mt-1 h-36 w-full rounded-[10px] border p-2 font-mono text-[10px] leading-tight ${dark ? 'border-white/[.14] bg-black/30 !text-white/80' : 'border-line bg-surface-subtle'}`} />
					<button type="button"
						onClick={() => { void copyText(getDiag()); }}
						className={`mt-1 w-full rounded-[10px] py-2 text-ui-xs transition ${dark ? 'bg-white/10 text-white/80 hover:bg-white/15' : 'bg-surface-muted text-fg hover:bg-hover-strong'}`}>{t('login.copyLog')}</button>
				</details>
				)}
			</div>
		</div>
	);
}

// 3색 브랜드 시그니처 (블랙 · 블루 · 레드) — 테마와 무관한 고정 아이덴티티 (2026-07-11 리뉴얼 스펙)
// 빨강(탑엔지니어링)/파랑(파워로직스)/검정 순 — 2026-07-13 사용자 지정 (기존 검/파/빨에서 재배치)
const BRAND_TRI = ['#E5484D', '#2F6BED', '#1B1E26'];

// ─── 입력 필드 좌측 아이콘 (User / Lock / Eye) + 버튼 스피너 ─────────────────────────
function UserIcon({ className }: { className?: string }) {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
			<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
			<circle cx="12" cy="7" r="4" />
		</svg>
	);
}

function GlobeIcon({ className }: { className?: string }) {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
			<circle cx="12" cy="12" r="9" />
			<path d="M3 12h18" />
			<path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
		</svg>
	);
}

function LockIcon({ className }: { className?: string }) {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
			<rect x="3" y="11" width="18" height="11" rx="2" />
			<path d="M7 11V7a5 5 0 0 1 10 0v4" />
		</svg>
	);
}

function EyeIcon({ off }: { off?: boolean }) {
	return off ? (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61" /><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><line x1="2" y1="2" x2="22" y2="22" />
		</svg>
	) : (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" /><circle cx="12" cy="12" r="3" />
		</svg>
	);
}

function SpinnerIcon() {
	return (
		<svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
			<path d="M21 12a9 9 0 1 1-6.2-8.56" />
		</svg>
	);
}
